// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import {
  useEvolvePaginatedGet,
  useEvolvePatch,
  type WorkCellTask,
  type WorkCellId,
  type Task,
  type UserId,
  type UpdateTaskBody,
  type CustomProperty,
  type UpdateTaskWorkCellBody,
} from '@evolvemep/api-ts';
import { LoadingOverlay } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { ChangeDataViewProvider } from 'components/Mantine/ChangeDataView/useChangeDataView';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { FacilityRequired, useFacilityRequired } from 'components/Mantine/GlobalFilters/FacilityRequired';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import type { EntityState } from 'components/Mantine/StatusEditor/types';
import { useCustomStatuses } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import type { EntityStateAgGridContext } from 'helpers/ag-grid/EntitySetFilterComponent';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import type { WithRequiredIsh } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';
import { getWorkCellsColDef } from 'modules/Shop/Tasks/columnDefs';
import { WorkCellsKanbanView } from 'modules/Shop/Tasks/WorkCellsKanbanView';
import { WorkCellTasksPauseAll } from 'modules/Shop/Tasks/WorkCellTasksPauseAll';

export type WorkCellsProps = {
  taskStatusTypes: EntityState<'Task'>[];
  workOrderStates: EntityState<'Work Order'>[];
  customTaskProperties: CustomProperty[];
};

const WorkCellsPageTable = ({ taskStatusTypes, customTaskProperties }: WorkCellsProps) => {
  const { stateColorMap } = useCustomStatuses('Task');
  const activeStatuses = useMemo(() => taskStatusTypes.filter((t) => t.name !== 'Completed'), [taskStatusTypes]);
  const {
    selectedFacility: { facilityId },
  } = useFacilityRequired();
  const { workCellIds, assigneeIds, activeAll } = useGlobalFilters({
    assignees: 'shop/task/assignedUsers',
    workCells: true,
    activeAll: {
      key: 'Tasks',
      allPossible: taskStatusTypes,
      whenActive: activeStatuses,
    },
  });

  const agGridContext = useMemo<EntityStateAgGridContext<'Task'>>(
    () => ({
      stateLabelMap: stateColorMap,
      entityStates: activeAll.value ?? taskStatusTypes,
    }),
    [stateColorMap, taskStatusTypes, activeAll.value],
  );

  const workCellsColDef = useMemo(
    () => getWorkCellsColDef(activeAll.state === 'active', customTaskProperties),
    [activeAll.state, customTaskProperties],
  );

  const {
    fetchPage,
    abortAll,
    setDefaultOpts: setDefaultOptsWorkCellTasks,
  } = useEvolvePaginatedGet('shop/workCellTask');
  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'work-cells',
    tableViewType: TABLE_VIEW_TYPE.TASKS,
    exportFileName: 'Tasks',
    colDef: workCellsColDef,
    fetchPage,
    abortAll,
    rowId: 'workCellTaskId',
    context: agGridContext,
  });

  useEffect(() => {
    // The default `taskStatusTypeIds` change when a user toggles the Open/Closed tabs,
    // so we use this effect to change the behavior of fetchPage.
    // Once fetchPage updates, the datasource will be recreated as part of a later useEffect
    // which will cause the entire Grid to reload.
    setDefaultOptsWorkCellTasks({
      defaultConfig: {
        params: {
          facilityIds: facilityId,
          taskStatusTypeIds: activeAll.value?.map((t) => t.stateId),
          workCellIds,
          assigneeIds,
        },
      },
    });
  }, [facilityId, setDefaultOptsWorkCellTasks, workCellIds, assigneeIds, activeAll.value]);

  const { apiCall: patchTask } = useEvolvePatch(`shop/task/:taskId`);
  const { apiCall: updateTaskToWorkCell } = useEvolvePatch(`shop/task/moveToWorkCell`);

  const patchWorkCellTask = async ({
    workCellTask,
    assignees,
    workCellId,
    ...patchBody
  }: WithRequiredIsh<{
    workCellTask: WorkCellTask;
    assignees?: UserId[];
    workCellId?: WorkCellId;
  }> &
    UpdateTaskBody) => {
    let updatedTask: WorkCellTask | Task = workCellTask;
    if (assignees) {
      updatedTask = await patchTask({ assignees }, { url: `shop/task/${workCellTask.taskId}` });
    }
    if (workCellId) {
      const updatedTasks = await updateTaskToWorkCell({
        taskIds: [workCellTask.taskId],
        workCellId,
      } as UpdateTaskWorkCellBody);
      updatedTask = updatedTasks.find((u) => u.taskId === workCellTask.taskId) ?? updatedTask;
    }
    if (
      strongEntries(patchBody).some(
        ([, b]) =>
          // `null` is a valid update, `undefined` is not
          b !== undefined,
      )
    ) {
      updatedTask = await patchTask(patchBody, { url: `shop/task/${workCellTask.taskId}` });
    }
    return {
      ...workCellTask,
      ...updatedTask,
    };
  };

  return (
    <>
      <BasePageHeader
        title="Tasks"
        topRightComponent={
          <WorkCellTasksPauseAll
            facilityId={facilityId}
            refresh={() => refreshGrid(gridApi)}
            taskStatusTypes={taskStatusTypes}
          />
        }
      />
      <AgGridEditable
        gridApi={gridApi}
        patchCall={patchWorkCellTask}
        configMapper={(workCellTask) => ({ url: `shop/task/${workCellTask.taskId}` })}
        requestConverter={(workCellTask, updates) => ({
          workCellTask,
          ...updates,
          assignees: updates.assignees?.map((u) => u.user.userId),
          workCellId: updates.workCellId,
          customProperties: updates.customProperties,
          // Predecessor updates are not handled via this grid
          taskPredecessorIds: undefined,
        })}
      >
        {(applyWrap) => (
          <AgGridReact<WorkCellTask>
            {...agGridProps}
            onFirstDataRendered={applyWrap}
            headerHeight={32}
            rowHeight={32}
          />
        )}
      </AgGridEditable>
    </>
  );
};

export const WorkCellTasksPage = () => {
  const { customProperties: customTaskProperties } = useCustomProperties('Task');
  const { states: workOrderStates } = useWorkOrderStatuses();
  const { states: taskStatusTypes } = useTaskStatuses();

  return (
    <FacilityRequired title="Tasks">
      {isNil(taskStatusTypes) || isNil(workOrderStates) || isNil(customTaskProperties) ? (
        <LoadingOverlay visible />
      ) : (
        <ChangeDataViewProvider name="Tasks" viewsToShow={['table', 'kanban']}>
          {(selectedView) => (
            <>
              {selectedView === 'table' && (
                <WorkCellsPageTable
                  taskStatusTypes={taskStatusTypes}
                  workOrderStates={workOrderStates}
                  customTaskProperties={customTaskProperties}
                />
              )}
              {selectedView === 'kanban' && (
                <WorkCellsKanbanView
                  taskStatusTypes={taskStatusTypes}
                  workOrderStates={workOrderStates}
                  customTaskProperties={customTaskProperties}
                />
              )}
            </>
          )}
        </ChangeDataViewProvider>
      )}
    </FacilityRequired>
  );
};
